import 'babel-polyfill';
import 'whatwg-fetch';
import "custom-event-polyfill";
import 'picturefill';
import noScroll from 'no-scroll';
import bowser from 'bowser';

import packageJson from './../../package.json';
import '../styles/_app.sass';
import './utils/element.matches.polyfill';
import './utils/u-100-vh';
import './utils/scrollbar';

import browserHistory from './utils/browser-history';
import closest from './utils/closest';

import scrollTo from './utils/scroll-to';

import pl from './page-loader';
import cb from './cookie-banner';
import lo from './loading-overlay';
import sl from './scroll-listener';
import h from './header';

if (('ontouchstart' in window) ||
  (window.DocumentTouch && document instanceof window.DocumentTouch)) {
  document.documentElement.classList.add('is-touch');
}

const PAGE_TRANSITION_DURATION = 1100;
let hasEndedPageTransition = true;

const domParser = new DOMParser();

let main = document.querySelector('.main');
let headerEl = document.querySelector('.header');
const head = document.querySelector('head');

let { location } = browserHistory;
const pageLoader = pl('page-loader').init();
const cookieBanner = cb('cookie-banner');
const scrollListener = sl('scroll-listener');
const header = h('header');
let loadingOverlay = lo('loading-overlay');

//
// Methods
//

function browserDetect() {
  document.documentElement.setAttribute('browser-name', bowser.name);
  document.documentElement.setAttribute('browser-version', bowser.version);
}

function checkAndImportModules(initRoot = document, modules = {}) {
  Object.keys(modules).forEach(nameSpace => {
    if (initRoot.querySelector(modules[nameSpace])) import(/* webpackChunkName: "chunk-[request]" */ `./${nameSpace}`).then(module => module.default(nameSpace, modules[nameSpace]).initAll(initRoot));
  })
}

function initModules(initRoot) {
  // modules here
  noScroll.off();
  // IMPORTANT: order is important
  // Key is module filename
  // Value is the selector
  checkAndImportModules(initRoot, {
    "block-img-load": 'img[data-block-img-load]', // module name: css selector
    "block-picture-load": 'picture[data-block-picture-load]',
    "article-card": '.article-card',
    "card": '.card',
    "hero-slider": '.hero-slider',
    "full-picture": '.full-picture',
    "gallery": ".gallery",
    "articles-slider": ".articles-slider",
    "cases-slider": ".cases-slider",
    "servizi-slider": ".servizi-slider",
    "pages-slider": ".pages-slider",
    "servizi": '.servizi',
    "case-list": '.case-list',
    "news-list": '.news-list',
    "sharer": '.share__link',
    "editorial-box": '.editorial-box',
    "editorial-full-picture": '.editorial-full-picture',
    "lightgallery-full-picture": '.lightgallery-full-picture',
    "manifesto": '.manifesto',
    "contatti": '.contatti',
    "g-map": '[data-g-map]',
    "contact-widget": '.contact-widget',
  })
}

function setMeta(doc) {
  document.title = doc.title;

  const newHead = doc.querySelector('head');

  // meta description => update, add and remove
  {
    const newEl = newHead.querySelector('meta[name="description"]');
    const el = head.querySelector('meta[name="description"]');
    if (newEl) {
      if (el) {
        // update existing meta
        el.setAttribute('content', newEl.getAttribute('content'));
      } else {
        // add new meta
        head.appendChild(newEl);
      }
    } else if (el) {
      // remove meta
      head.removeChild(el);
    }
  }

  // og => update and add
  [...newHead.querySelectorAll('meta[property^="og:"]')].forEach((newEl) => {
    let el;
    if (el === head.querySelector(`meta[property="${newEl.getAttribute('property')}"]`)) {
      // update existing meta
      el.setAttribute('content', newEl.getAttribute('content'));
    } else {
      // add new meta
      head.appendChild(newEl);
    }
  });

  // og => remove
  [...head.querySelectorAll('meta[property^="og:"]')].forEach((el) => {
    if (!head.querySelector(`meta[property="${el.getAttribute('property')}"]`)) {
      // removed meta
      head.removeChild(el);
    }
  });
}

function setCurrentLinks() {
  const $allLinks = document.querySelectorAll(`a[href], [data-href]`);
  [...$allLinks].forEach($link => {
    if ($link.href || $link.getAttribute('data-href')) {
      const reg = new RegExp(`^${$link.href || $link.getAttribute('data-href')}`)
      if (window.location.href.match(reg)) {
        $link.classList.add('is-in-current-url');
      }
      if (window.location.href === $link.href || $link.getAttribute('data-href')) {
        $link.classList.add('is-current-page');
      }
    }
  })
}

function changePage(newLocation) {
  hasEndedPageTransition = false;
  loadingOverlay.show();
  document.dispatchEvent(new CustomEvent('page-will-change'));

  fetch(newLocation.pathname)
  .then((response) => {
    // default+404
    if ((response.status >= 200 && response.status < 300) || response.status === 404) {
      return response.text();
    }
    throw new Error(`Failed response: ${JSON.stringify(response)}`);
  })
  .then((body) => {
    const doc = domParser.parseFromString(body, 'text/html');
    const newMain = doc.querySelector('.main');
    const newHeaderEl = doc.querySelector('.header');

    if (newMain === null || newHeaderEl === null) {
      throw new Error(`Invalid HTML fetched`);
    }

    initModules(newMain);

    document.documentElement.Component.pageTransition = () => {
      headerEl.parentNode.replaceChild(newHeaderEl, headerEl); // Needs replacing due to theme change, and tag differences on logo element
      main.classList.add('main--exit-animation')
      newMain.classList.add('main--enter-animation')
      main.parentNode.insertBefore(newMain, main);
      document.body.className = doc.body.className;

      if (bowser.name === 'Internet Explorer' && global.picturefill) {
        global.picturefill();
      }

      header.init()
      setMeta(doc);
      setCurrentLinks();
      loadingOverlay.success();

      // Reset Scroll Top
      document.documentElement.scrollTop = 0;
      document.body.scrollTop = 0;
  
      setTimeout(() => {
        main.parentNode.removeChild(main);
        newMain.classList.remove('main--enter-animation')
        main = newMain;
        headerEl = newHeaderEl;
    
        // google analytics
        if (global.gtag) {
          global.gtag('event', 'page_view', {'send_to' : 'UA-XXXXXXXX-1', 'page_path': newLocation.pathname});
        }
  
        document.dispatchEvent(new CustomEvent('page-has-changed'));
        loadingOverlay.hide();
        hasEndedPageTransition = true;
        location = newLocation;

        if (newLocation.hash && newLocation.hash !== '') {
          // Scroll To Hash
          scrollTo(main.querySelector(newLocation.hash), 0, 1500);
        } else {
          document.documentElement.scrollTop = 0;
          document.body.scrollTop = 0;
        }
      }, PAGE_TRANSITION_DURATION)
    }

    if (typeof newMain.dataset.notReadyPage === 'undefined' || newMain.dataset.notReadyPage === 'false') document.documentElement.Component.pageTransition();
  })
  .catch((error) => {
    console.error(error)
    browserHistory.replace(location.pathname);
    loadingOverlay.hide();
    hasEndedPageTransition = true;
  });
}

//
// Procedures
//

// let app export to be accessible to other modules with a fake setTimeout
// And see some waiting animation
setTimeout(() => {
  initModules(main);
  browserDetect();
  setCurrentLinks()
  cookieBanner.init();
  scrollListener.init();
  header.init();
  loadingOverlay = loadingOverlay.init();
  if (typeof main.dataset.notReadyPage === 'undefined' || main.dataset.notReadyPage === 'false') {
    pageLoader.hide();
  }
}, 333);

// hash on init, first page
if (window.location.hash && window.location.hash !== '') {
  scrollTo(document.querySelector(window.location.hash), 0, 1300);
}

//
// Listeners / Observers
//

// single click listner for internal nav handling
document.addEventListener('click', (e) => {
  const anchor = closest({ el: e.target, selector: 'a' });
  // page navigation or view navigation
  if (anchor && anchor.href) {
    if (
      window.location.hostname === anchor.hostname
      && (!anchor.getAttribute('target') || anchor.getAttribute('target') !== '_blank')
    ) {
      // if same host we will use internal ajax navigation
      e.preventDefault();
      browserHistory.push(anchor.pathname + anchor.search + anchor.hash);
    }
  }
  return true;
});

// listen for changes to the location
browserHistory.listen((newLocation, action) => {
  if (action !== 'REPLACE') {
    if (newLocation.pathname === location.pathname) {
      scrollTo(newLocation.hash ? main.querySelector(newLocation.hash) : main, 0, 1500)
    } else {
      if (hasEndedPageTransition) {
        changePage(newLocation);
      }
    }
  }
});

document.documentElement.Component = {
  name: packageJson.name,
  pageTransition: () => {
    loadingOverlay.success();
    setTimeout(() => loadingOverlay.hide(), 20);
  }
}

export default document.documentElement.Component;



// WEBPACK FOOTER //
// ./src/scripts/_app.js